export interface CameraStartOptions {
  withAudio: boolean;
}

export class CameraController {
  private stream: MediaStream | null = null;
  private generation = 0;

  constructor(private readonly video: HTMLVideoElement) {}

  async start(options: CameraStartOptions): Promise<MediaStream> {
    this.stop();
    const generation = this.generation;
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('当前浏览器不支持摄像头采集');
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'user',
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 24, max: 30 },
      },
      audio: options.withAudio
        ? {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          }
        : false,
    });
    // 授权弹窗期间可能已重置页面；晚到的流必须立即关闭，不能重新点亮相机。
    if (generation !== this.generation) {
      stream.getTracks().forEach((track) => track.stop());
      throw new Error('摄像头启动已取消');
    }
    this.stream = stream;
    try {
      this.video.srcObject = this.stream;
      this.video.muted = true;
      this.video.playsInline = true;
      await this.video.play();
      if (generation !== this.generation) throw new Error('摄像头启动已取消');
      return stream;
    } catch (error) {
      // 浏览器拒绝播放或页面状态异常时立即释放硬件，避免授权成功后摄像头仍被占用。
      if (generation === this.generation) this.stop();
      throw error;
    }
  }

  get activeStream(): MediaStream {
    if (!this.stream || this.stream.getVideoTracks().every((track) => track.readyState === 'ended')) {
      throw new Error('摄像头尚未启动');
    }
    return this.stream;
  }

  stop(): void {
    this.generation += 1;
    if (this.stream) {
      // 视频和音频轨必须逐条停止，仅清空 video.srcObject 不会关闭真实设备。
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    this.video.srcObject = null;
  }
}
